import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';
import { baseAPI } from '../../../config';
import userStore from '../../../store/userStore';
import {
  TASK_TYPES,
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_PRIORITIES,
  ASSIGNMENT_STATUS_LABELS,
  ASSIGNMENT_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
} from '@claimup/shared/constants/assignments';
import {
  FILTERABLE_EXPEDIENT_STATUSES,
  ALL_EXPEDIENT_TYPES,
  EXPEDIENT_TYPE_LABELS,
  EXPEDIENT_TYPES,
  expedientStatusLabel,
} from '@claimup/shared/constants/expedient';

/** El tipo que aplica el backend en «Expedientes sin tarea» si no se pide otro. */
const DEFAULT_EXPEDIENT_TYPE_FILTER = EXPEDIENT_TYPES.EXTRA_JUDICIAL;

const formatUser = u => `${u.username || ''} ${u.userlastname || ''}`.trim() || u.usermail;

const inputClassName =
  'w-full form-input text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50';

/**
 * Campo de texto que solo escribe en la URL al pulsar Enter o salir del campo:
 * cada cambio de URL relanza la consulta, y no queremos una por tecla.
 */
const CommittedInput = ({ value, onCommit, ...props }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft.trim() !== value) onCommit(draft.trim());
  };

  return (
    <input
      {...props}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
      }}
      className={inputClassName}
    />
  );
};

/**
 * `mode='tasks'`: la lista de tareas, con todos los filtros.
 * `mode='expedients'`: la de expedientes sin tarea. Ahí no hay tarea que
 * filtrar (fecha, asignado, tipo, prioridad), y sí el tipo de expediente.
 */
const AssignmentFilters = ({ mode = 'tasks' }) => {
  const isTasks = mode === 'tasks';
  const { user } = userStore();
  const esAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  // Los filtros de todos los días a la vista; el resto, tras «Más filtros». Si
  // alguno de esos ya está puesto (viene en la URL), se abren solos: esconder
  // un filtro activo es engañar sobre lo que se ve.
  const AVANZADOS = ['start_date', 'end_date', 'priority', 'estado', 'importe_min', 'importe_max'];
  const [masFiltros, setMasFiltros] = useState(() => AVANZADOS.some(k => searchParams.get(k)));
  const avanzado = masFiltros ? '' : 'hidden';

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const fetchUsers = async () => {
      try {
        // `/assignments/team`: al admin, el personal de ClaimUp; a la empresa y
        // su gestor, sus gestores. `/users/list` es solo del admin y a ellos les
        // dejaba el filtro vacío.
        const response = await axios.get(`${baseAPI}/assignments/team`, { headers });
        setUsers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching users for filters:', error);
      }
    };
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${baseAPI}/users/list?role=client,company`, { headers });
        setClients(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching clients for filters:', error);
      }
    };
    fetchUsers();
    // Filtrar por cliente es del admin: la empresa solo ve lo suyo.
    if (esAdmin) fetchClients();
  }, [esAdmin]);

  // Helper to convert URL param string to select options
  const parseMultiSelectParam = (paramValue, labelsMap) => {
    if (!paramValue) return [];
    return paramValue.split(',').map(value => ({
      value,
      label: labelsMap[value],
    }));
  };

  // Helper to convert select options to URL param string
  const serializeMultiSelectOptions = options => {
    if (!options || options.length === 0) return null;
    return options.map(opt => opt.value).join(',');
  };

  const handleMultiSelectChange = (filterName, selectedOptions) => {
    const newParams = new URLSearchParams(searchParams);
    const serializedValue = serializeMultiSelectOptions(selectedOptions);

    if (serializedValue) {
      newParams.set(filterName, serializedValue);
    } else {
      newParams.delete(filterName);
    }

    // Reset to page 1 when changing filters
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleSimpleParamChange = (filterName, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === '' || value == null) {
      newParams.delete(filterName);
    } else {
      newParams.set(filterName, value);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleTimeFilterChange = value => {
    const newParams = new URLSearchParams(searchParams);
    if (value === '' || value === 'all') {
      newParams.delete('time');
    } else {
      newParams.set('time', value);
    }
    // Reset to page 1 when changing filters
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Parse current values from URL
  const currentStatus = parseMultiSelectParam(searchParams.get('status'), ASSIGNMENT_STATUS_LABELS);
  const currentTaskType = parseMultiSelectParam(searchParams.get('task_type'), TASK_TYPE_LABELS);
  const currentPriority = parseMultiSelectParam(searchParams.get('priority'), ASSIGNMENT_PRIORITY_LABELS);
  const currentTimeFilter = searchParams.get('time') || 'all';
  const currentStartDate = searchParams.get('start_date') || '';
  const currentEndDate = searchParams.get('end_date') || '';
  const currentAssignedTo = searchParams.get('assigned_to') || '';
  const currentClientId = searchParams.get('client_id') || '';
  const currentEstado = (searchParams.get('estado') || '')
    .split(',')
    .filter(Boolean)
    .map(value => ({ value, label: value }));
  const currentDeudor = searchParams.get('deudor') || '';
  const currentImporteMin = searchParams.get('importe_min') || '';
  const currentImporteMax = searchParams.get('importe_max') || '';

  const hasDateRange = Boolean(currentStartDate || currentEndDate);

  const timeFilters = [
    { value: 'all', label: 'Todos' },
    { value: 'today', label: 'Hoy' },
    { value: 'overdue', label: 'Vencidas' },
    { value: 'week', label: 'Esta semana' },
    { value: 'month', label: 'Este mes' },
  ];

  // Create options for react-select
  const statusOptions = ASSIGNMENT_STATUSES.map(status => ({
    value: status,
    label: ASSIGNMENT_STATUS_LABELS[status],
  }));

  const taskTypeOptions = TASK_TYPES.map(type => ({
    value: type,
    label: TASK_TYPE_LABELS[type],
  }));

  const priorityOptions = ASSIGNMENT_PRIORITIES.map(priority => ({
    value: priority,
    label: ASSIGNMENT_PRIORITY_LABELS[priority],
  }));

  const assignedToOptions = [
    { value: '', label: 'Todos' },
    { value: 'unassigned', label: 'Sin asignar' },
    { value: 'AIAgent', label: 'IA (automática)' },
    ...users.map(user => ({
      value: user._id,
      label: formatUser(user),
    })),
  ];

  const currentAssignedToOption =
    assignedToOptions.find(opt => opt.value === currentAssignedTo) || assignedToOptions[0];

  const clientOptions = clients
    .map(client => ({ value: client._id, label: formatUser(client) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  const currentClientOption = clientOptions.find(opt => opt.value === currentClientId) || null;

  const estadoOptions = FILTERABLE_EXPEDIENT_STATUSES.map(estado => ({
    value: estado,
    label: expedientStatusLabel(estado),
  }));

  // Sin `type` en la URL el backend aplica extrajudicial; «all» quita el filtro.
  const currentExpedientType = searchParams.get('type') || DEFAULT_EXPEDIENT_TYPE_FILTER;
  const expedientTypeOptions = [
    ...ALL_EXPEDIENT_TYPES.map(type => ({ value: type, label: EXPEDIENT_TYPE_LABELS[type] || type })),
    { value: 'all', label: 'Todos' },
  ];

  // Custom styles for react-select
  const customSelectStyles = {
    control: base => ({
      ...base,
      minHeight: '38px',
      fontSize: '0.875rem',
      borderColor: '#cbd5e1',
      borderRadius: '0.5rem',
      '&:hover': {
        borderColor: '#cbd5e1',
      },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.875rem',
      backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#e0e7ff' : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      '&:hover': {
        backgroundColor: state.isSelected ? '#4f46e5' : '#e0e7ff',
      },
    }),
    multiValue: base => ({
      ...base,
      backgroundColor: '#e0e7ff',
      borderRadius: '0.375rem',
    }),
    multiValueLabel: base => ({
      ...base,
      color: '#4338ca',
      fontSize: '0.75rem',
    }),
    multiValueRemove: base => ({
      ...base,
      color: '#4338ca',
      '&:hover': {
        backgroundColor: '#c7d2fe',
        color: '#3730a3',
      },
    }),
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
      {isTasks && (
        <>
          {/* Time Filter */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Tiempo</label>
            <select
              value={currentTimeFilter}
              onChange={e => handleTimeFilterChange(e.target.value)}
              disabled={hasDateRange}
              className={`w-full form-select text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${
                hasDateRange ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''
              }`}
            >
              {timeFilters.map(filter => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
            {hasDateRange && (
              <p className='text-[11px] text-slate-400 mt-1'>Desactivado mientras hay rango de fechas</p>
            )}
          </div>

          {/* Date range: start */}
          <div className={avanzado}>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Desde</label>
            <input
              type='date'
              value={currentStartDate}
              onChange={e => handleSimpleParamChange('start_date', e.target.value)}
              max={currentEndDate || undefined}
              className='w-full form-input text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
            />
          </div>

          {/* Date range: end */}
          <div className={avanzado}>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Hasta</label>
            <input
              type='date'
              value={currentEndDate}
              onChange={e => handleSimpleParamChange('end_date', e.target.value)}
              min={currentStartDate || undefined}
              className='w-full form-input text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
            />
          </div>

          {/* Assigned to */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Asignado a</label>
            <Select
              value={currentAssignedToOption}
              onChange={option => handleSimpleParamChange('assigned_to', option?.value || '')}
              options={assignedToOptions}
              styles={customSelectStyles}
              placeholder='Todos'
              isClearable={false}
            />
          </div>

          {/* Status Filter - Multi Select */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Estado de la tarea</label>
            <Select
              isMulti
              value={currentStatus}
              onChange={selectedOptions => handleMultiSelectChange('status', selectedOptions)}
              options={statusOptions}
              styles={customSelectStyles}
              placeholder='Todos'
              isClearable={false}
              closeMenuOnSelect={false}
            />
          </div>

          {/* Task Type Filter - Multi Select */}
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Tipo</label>
            <Select
              isMulti
              value={currentTaskType}
              onChange={selectedOptions => handleMultiSelectChange('task_type', selectedOptions)}
              options={taskTypeOptions}
              styles={customSelectStyles}
              placeholder='Todos'
              isClearable={false}
              closeMenuOnSelect={false}
            />
          </div>

          {/* Priority Filter - Multi Select */}
          <div className={avanzado}>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Prioridad</label>
            <Select
              isMulti
              value={currentPriority}
              onChange={selectedOptions => handleMultiSelectChange('priority', selectedOptions)}
              options={priorityOptions}
              styles={customSelectStyles}
              placeholder='Todas'
              isClearable={false}
              closeMenuOnSelect={false}
            />
          </div>
          {/* La fase: el admin trabaja recobro; lo AR lo lleva el cliente y
              aquí solo se mira si se pide. Al gestor ya le llega solo lo AR. */}
          {esAdmin && (
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1' htmlFor='filtro-fase'>
                Fase
              </label>
              <select
                id='filtro-fase'
                value={searchParams.get('phase') || ''}
                onChange={e => handleSimpleParamChange('phase', e.target.value)}
                className='w-full form-select text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
              >
                <option value=''>Recobro</option>
                <option value='ar'>AR (supervisión)</option>
                <option value='all'>Todas</option>
              </select>
            </div>
          )}
        </>
      )}

      {/* Filtros del expediente */}
      <div>
        <label className='block text-sm font-medium text-slate-700 mb-1'>Deudor</label>
        <CommittedInput
          type='search'
          value={currentDeudor}
          onCommit={value => handleSimpleParamChange('deudor', value)}
          placeholder='Nombre del deudor'
        />
      </div>

      {esAdmin && (
        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>Cliente</label>
          <Select
            value={currentClientOption}
            onChange={option => handleSimpleParamChange('client_id', option?.value || '')}
            options={clientOptions}
            styles={customSelectStyles}
            placeholder='Todos'
            isClearable
          />
        </div>
      )}

      <div className={avanzado}>
        <label className='block text-sm font-medium text-slate-700 mb-1'>Estado del expediente</label>
        <Select
          isMulti
          value={currentEstado}
          onChange={selectedOptions => handleMultiSelectChange('estado', selectedOptions)}
          options={estadoOptions}
          styles={customSelectStyles}
          placeholder={isTasks ? 'Todos' : 'En gestión (On going, Hot, Cold…)'}
          isClearable={false}
          closeMenuOnSelect={false}
        />
      </div>

      {!isTasks && (
        <div>
          <label className='block text-sm font-medium text-slate-700 mb-1'>Tipo de expediente</label>
          <select
            value={currentExpedientType}
            onChange={e =>
              handleSimpleParamChange('type', e.target.value === DEFAULT_EXPEDIENT_TYPE_FILTER ? '' : e.target.value)
            }
            className='w-full form-select text-sm border-slate-300 rounded-lg focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
          >
            {expedientTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={avanzado}>
        <label className='block text-sm font-medium text-slate-700 mb-1'>Importe deuda (€)</label>
        <div className='flex items-center gap-2'>
          <CommittedInput
            type='number'
            min='0'
            value={currentImporteMin}
            onCommit={value => handleSimpleParamChange('importe_min', value)}
            placeholder='Mín.'
            aria-label='Importe mínimo'
          />
          <span className='text-slate-400'>–</span>
          <CommittedInput
            type='number'
            min='0'
            value={currentImporteMax}
            onCommit={value => handleSimpleParamChange('importe_max', value)}
            placeholder='Máx.'
            aria-label='Importe máximo'
          />
        </div>
      </div>

      <div className='flex items-end'>
        <button
          type='button'
          onClick={() => setMasFiltros(v => !v)}
          className='text-sm font-medium text-blue-700 hover:underline'
          aria-expanded={masFiltros}
        >
          {masFiltros ? 'Menos filtros' : 'Más filtros'}
        </button>
      </div>
    </div>
  );
};

export default AssignmentFilters;
